import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetEmailTemplateByIdQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
} from '../../../store/api/emailTemplateApi';

const TEMPLATE_TYPES = [
  { id: 'sales_invoice', label: 'Sales Invoice' },
  { id: 'quotation', label: 'Quotation' },
  { id: 'purchase_order', label: 'Purchase Order' },
];

const TEMPLATE_HELP = {
  sales_invoice: [
    '{{customerName}}',
    '{{invoiceNumber}}',
    '{{totalAmount}}',
    '{{balanceAmount}}',
    '{{invoiceDate}}',
  ],
  quotation: ['{{customerName}}', '{{quotationNumber}}', '{{totalAmount}}', '{{expiryDate}}'],
};

function EmailTemplateForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    type: 'sales_invoice',
    isDefault: false,
  });

  const { data: template, isLoading } = useGetEmailTemplateByIdQuery(id, { skip: !isEdit });
  const [createTemplate, { isLoading: creating }] = useCreateEmailTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateEmailTemplateMutation();

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        subject: template.subject,
        body: template.body,
        type: template.type,
        isDefault: template.isDefault,
      });
    }
  }, [template]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await updateTemplate({ id, ...formData }).unwrap();
      } else {
        await createTemplate(formData).unwrap();
      }
      navigate('/settings?tab=2');
    } catch (err) {
      console.error(err);
      alert('Failed to save template');
    }
  };

  if (isLoading) return <CircularProgress />;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/settings?tab=2')}
        sx={{ mb: 2 }}
      >
        Back to Email Settings
      </Button>

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        {isEdit ? 'Edit Email Template' : 'Create Email Template'}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Template Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="e.g. Standard Invoice Email"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      select
                      label="Template Type"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      {TEMPLATE_TYPES.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ mt: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isDefault}
                            onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                          />
                        }
                        label="Set as Default for this type"
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Email Subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      placeholder="Invoice {{invoiceNumber}} from {{companyName}}"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Email Body (HTML allowed)"
                      value={formData.body}
                      onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                      multiline
                      rows={12}
                      required
                      placeholder="Dear {{customerName}}, ..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2, bgcolor: 'info.soft' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Available Placeholders
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                Use these tags to dynamically insert order information.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {TEMPLATE_HELP[formData.type]?.map((tag) => (
                  <Box
                    key={tag}
                    sx={{
                      bgcolor: 'background.paper',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      fontFamily: 'monospace',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {tag}
                  </Box>
                )) || <Typography variant="caption">Select a type to see placeholders</Typography>}
              </Box>
            </Paper>

            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                startIcon={creating || updating ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={creating || updating}
              >
                {isEdit ? 'Update Template' : 'Create Template'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default EmailTemplateForm;
